import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { X, ChevronLeft, ChevronRight, UserPlus, ShoppingCart, CreditCard, MapPin } from "lucide-react";
import biryaniImage from "@/assets/biryani-branded-watermark.jpg";
import { cn } from "@/lib/utils";

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  position: "center" | "top" | "bottom";
}

const tourSteps: TourStep[] = [
  {
    id: "welcome",
    title: "Welcome to Bawarchi 149! 🍛",
    description: "Let us show you how easy it is to order authentic Hyderabadi Biryani. This quick tour will guide you through the process.",
    icon: <img src={biryaniImage} alt="Biryani" className="h-12 w-12 rounded-full object-cover" />,
    position: "center",
  },
  {
    id: "signup",
    title: "Step 1: Create Your Account",
    description: "Tap 'Sign In' in the header, then choose 'Sign Up'. Enter your email and create a password. It's quick and secure!",
    icon: <UserPlus className="h-8 w-8 text-primary" />,
    position: "top",
  },
  {
    id: "order",
    title: "Step 2: Place Your Order",
    description: "Browse our menu, select your favorite biryani, choose quantity, and tap 'Order Now' for same-day delivery or 'Pre-Order' for advance booking.",
    icon: <img src={biryaniImage} alt="Biryani" className="h-12 w-12 rounded-full object-cover" />,
    position: "center",
  },
  {
    id: "payment",
    title: "Step 3: Choose Payment",
    description: "Pay via Cash on Delivery or GCash. For GCash, enter your reference number and upload payment proof for faster processing.",
    icon: <CreditCard className="h-8 w-8 text-primary" />,
    position: "center",
  },
  {
    id: "track",
    title: "Step 4: Track Your Order",
    description: "After ordering, go to 'My Orders' to track your order status in real-time. You'll also receive WhatsApp updates!",
    icon: <MapPin className="h-8 w-8 text-primary" />,
    position: "bottom",
  },
];

interface OnboardingTourProps {
  onComplete: () => void;
}

const OnboardingTour = ({ onComplete }: OnboardingTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    setIsVisible(false);
    localStorage.setItem("onboarding_completed", "true");
    onComplete();
  };

  const handleSkip = () => {
    handleComplete();
  };

  if (!isVisible) return null;

  const step = tourSteps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
      <Card className={cn(
        "w-[90%] max-w-md mx-4 shadow-2xl border-primary/20",
        step.position === "top" && "mt-20",
        step.position === "bottom" && "mb-20"
      )}>
        <CardContent className="p-6">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8"
            onClick={handleSkip}
            aria-label="Close tour"
          >
            <X className="h-4 w-4" />
          </Button>

          {/* Step indicator */}
          <div className="flex justify-center gap-1.5 mb-4">
            {tourSteps.map((_, index) => (
              <div
                key={index}
                className={cn(
                  "h-1.5 rounded-full transition-all duration-300",
                  index === currentStep 
                    ? "w-6 bg-primary" 
                    : index < currentStep 
                      ? "w-1.5 bg-primary/60" 
                      : "w-1.5 bg-muted"
                )}
              />
            ))}
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-full bg-primary/10">
              {step.icon}
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-foreground mb-2">
              {step.title}
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="flex-1"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            
            <Button
              onClick={handleNext}
              className="flex-1"
            >
              {currentStep === tourSteps.length - 1 ? (
                "Get Started!"
              ) : (
                <>
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>

          {/* Skip link */}
          <button
            onClick={handleSkip}
            className="w-full text-center text-sm text-muted-foreground mt-4 hover:text-foreground transition-colors"
          >
            Skip tour
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingTour;
